<template>
    <div class="main-content">
        <PurchaseMultipleLicenseOptions v-if="!isUpgradeMode && !isGiftMode" class="mb-4"  />
        
        <div class="main-header">
            <div class="plan-section">
                <p>{{ isUpgradeMode ? 'Modify Plan' : 'Select Plan' }}</p>
            </div>
            <div class="h-11 mb-0 sm:mb-2 w-auto sm:w-auto sm:flex-shrink-0">
                <PrimeTabs class="h-9 w-auto sm:w-auto" :value="String(selectedTerm)" @update:value="onTabChange">
                    <PrimeTabList class="mobile-no-wrap-tabs w-auto sm:w-auto">
                        <PrimeTab 
                            v-for="tab in tabs" 
                            :key="tab.title" 
                            :value="String(tab.value)"
                            :class="{
                                'current-term-highlight': isUpgradeMode && license?.term === tab.value,
                                'upgrade-term-highlight': isUpgradeMode && license?.slug === selectedPlanType && selectedTerm === tab.value && license?.term !== tab.value
                            }"
                            class="mobile-responsive-tab"
                        >
                            {{ tab.title }}
                            <span v-if="isUpgradeMode && license?.term === tab.value" class="current-badge">Current</span>
                            <span v-if="isUpgradeMode && selectedTerm === tab.value && license?.term !== tab.value" class="upgrade-badge">New</span>
                            <!-- Show available licenses for selected plan type and timeframe -->
                            <span v-if="purchaseType === PurchaseType.GIFT && getAvailableLicensesForTerm(tab.value) > 0" class="available-badge">
                                {{ getAvailableLicensesForTerm(tab.value) }}
                            </span>
                        </PrimeTab>
                    </PrimeTabList>
                </PrimeTabs>
            </div>
        </div>

        <PurchaseInformation v-if="selectedTerm === LicenseRenewalTerm.MONTHLY" />

        <EssentialsLicenseOption />
        <ProLicenseOption />
        <SignatureLicenseOption />
    </div>
</template>

<script setup lang="ts">
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { LicenseRenewalTerm, LicenseType, PurchaseType } from '~/types/licenses/licenses';
import EssentialsLicenseOption from './Licenses/EssentialsLicenseOption.vue';
import ProLicenseOption from './Licenses/ProLicenseOption.vue';
import SignatureLicenseOption from './Licenses/SignatureLicenseOption.vue';
import PurchaseInformation from './PurchaseInformation.vue';
import PurchaseMultipleLicenseOptions from './PurchaseMultipleLicenseOptions.vue';

const tabs = [
    { title: 'Monthly', value: LicenseRenewalTerm.MONTHLY },
    { title: '1 Year', value: LicenseRenewalTerm.YEARLY },
    { title: '2 Years', value: LicenseRenewalTerm.TWO_YEARS },
    { title: '3 Years', value: LicenseRenewalTerm.THREE_YEARS },
];

const {selectedTerm, purchaseType, license, selectedPlanType} = storeToRefs(useSubscribeStore());
const personStore = usePersonStore();

const isUpgradeMode = computed(() => purchaseType.value === PurchaseType.UPGRADE);
const isGiftMode = computed(() => purchaseType.value === PurchaseType.GIFT);

// Get available licenses for a specific plan type and term
const getAvailableLicensesForTerm = (term: LicenseRenewalTerm) => {
    if (!personStore.person?.licenses) return 0;
    // If no plan type is selected, show total available licenses across all plans for this term
    if (!selectedPlanType.value) {
        return personStore.person.licenses.filter(license => {
            return license.term === term && 
                   license.expired === false && 
                   !license.assigned_to;
        }).length;
    }
    
    // If plan type is selected, show available licenses for that specific plan and term
    const count= personStore.person.licenses.filter(license => {
        return license.term === term && 
               license.expired === false && 
               !license.assigned_to;
    }).length;

    return count;
};

//handle tab change
const onTabChange = (value: LicenseRenewalTerm) => {
    selectedTerm.value = Number(value);
    
    // If switching to Monthly and Signature is selected, unselect it and switch to Pro
    if (Number(value) === LicenseRenewalTerm.MONTHLY && selectedPlanType.value === LicenseType.SIGNATURE) {
        selectedPlanType.value = LicenseType.PRO;
    }
};
</script>

<style lang="scss" scoped>
.main-content {
    @apply w-full lg:max-w-2xl;
}

.essential-plan {
    @apply flex flex-wrap items-start content-start self-stretch sm:pt-6 sm:pb-6 sm:pl-8 sm:pr-8 p-4 gap-x-8 gap-y-4;
    @apply border border-zinc-300;
}

.main-header {
    @apply flex flex-row items-center justify-between gap-4 pb-2 sm:pb-3 self-stretch;
}

.plan-section {
    @apply flex items-center gap-2 flex-shrink-0;

    p {
        @apply text-zinc-900 font-inter text-xl font-semibold leading-8;
    }
}

.current-term-highlight {
    @apply bg-green-100 border-green-300;
}

.upgrade-term-highlight {
    @apply bg-blue-300 border-blue-300;
}

.current-badge {
    @apply ml-2 px-2 py-1 text-xs bg-green-500 text-white rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
}

.upgrade-badge {
    @apply ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
}

.available-badge {
    @apply ml-2 px-2 py-1 text-xs bg-violet-500 text-white rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
}

/* Mobile-only fixes for tab wrapping */
@media (max-width: 639px) {
    .main-header {
        /* Stack vertically on mobile so tabs go under "Modify Plan" */
        @apply flex-col items-start gap-2;
    }
    
    // .main-header > div:last-child {
    //     /* Tab container should only take needed width on mobile */
    //     width: auto !important;
    // }
    
    .main-header > div:last-child {
        /* Force PrimeTabs to take full width on mobile */
        :deep(.p-tabs) {
            width: 100% !important;
            display: flex !important;
            flex-grow: 1;
        }
        
        :deep(.p-tablist) {
            width: 100% !important;
            display: flex !important;
            flex-grow: 1;
        }
        
        /* Force individual tab buttons to stretch on mobile */
        :deep(.p-tab) {
            flex: 1 1 0px !important;
            min-width: 0 !important;
            width: auto !important;
        }
        
        :deep(.p-tab-header-content) {
            width: 100% !important;
            display: flex !important;
            justify-content: center !important;
        }
    }

    .mobile-no-wrap-tabs {
        @apply flex w-full;
        /* Tabs stretch to fill all available space on mobile */
        flex-direction: row !important;
        justify-content: stretch;
        gap: 4px;
        flex: 1 1 0px !important;
    }
    
    .mobile-responsive-tab {
        @apply px-2 py-1 text-xs text-center;
        /* Each tab takes equal space and stretches */
        flex: 1 1 0px !important;
        min-width: 0 !important;
        width: auto !important;
        
        /* Reduce badge sizes on mobile */
        .current-badge,
        .upgrade-badge,
        .available-badge {
            @apply ml-1 px-1 py-0.5 text-xs inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
        }
    }
}

/* Desktop - normal tab behavior */
@media (min-width: 640px) {
    .main-header {
        /* Keep Select Plan on left, tabs center-aligned and flex to right */
        @apply flex-row items-center gap-4;
    }
    
    .main-header > div:last-child {
        /* Tab container takes natural width, center-aligned with Select Plan */
        width: auto !important;
        flex-grow: 0;
        flex-shrink: 0;
        
        /* PrimeTabs take natural width */
        :deep(.p-tabs) {
            width: auto !important;
            display: flex !important;
        }
        
        :deep(.p-tablist) {
            width: auto !important;
            display: flex !important;
        }
        
        /* Individual tab buttons take natural width */
        :deep(.p-tab) {
            flex: 0 0 auto !important;
            min-width: auto !important;
            width: auto !important;
        }
        
        :deep(.p-tab-header-content) {
            width: auto !important;
            display: flex !important;
            justify-content: center !important;
        }
    }
    
    .mobile-no-wrap-tabs {
        /* Desktop tabs take natural width */
        width: auto !important;
        display: flex !important;
        flex: 0 0 auto !important;
        gap: 6px;
    }
    
    .mobile-responsive-tab {
        /* Tabs take natural width on desktop */
        @apply px-3 py-2 text-sm text-center;
        flex: 0 0 auto !important;
        min-width: auto !important;
        width: auto !important;
        
        /* Normal badge sizes on desktop */
        .current-badge,
        .upgrade-badge,
        .available-badge {
            @apply ml-2 px-2 py-1 text-xs inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
        }
    }
}
</style>
